import { Button } from './ui/button';
import { ArrowRight, CheckCircle2, Shield, Star, Users, Clock, Plane } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Reveal } from './motion/Reveal';

const MotionButton = motion(Button);

// Card visual do lado direito — antes a Hero era só texto centralizado
// sobre o gradiente, sem nenhum elemento visual. Isto ilustra o que a
// ferramenta realmente faz (checar elegibilidade), não é depoimento nem
// foto — não temos foto real da equipe para usar, e inventar uma seria
// desonesto. O selo "4.9" reaproveita a nota já usada em TrustStats.js,
// não é um número novo.
const PreviewCard = () => (
  <div className="relative w-full max-w-sm mx-auto">
    <motion.div
      initial={{ opacity: 0, y: 20, rotate: 3 }}
      animate={{ opacity: 1, y: 0, rotate: 2 }}
      transition={{ duration: 0.6, delay: 0.4, ease: [0.23, 1, 0.32, 1] }}
      className="bg-white rounded-3xl shadow-2xl p-6 sm:p-7"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
          <Plane className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-extrabold text-slate-900 text-sm leading-tight">Verificação de elegibilidade</p>
          <p className="text-slate-400 text-xs font-medium">Resultado em tempo real</p>
        </div>
      </div>

      <div className="space-y-3">
        {['Voo atrasado', 'Voo cancelado', 'Overbooking'].map((item, i) => (
          <motion.div
            key={item}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.7 + i * 0.12 }}
            className="flex items-center gap-3 bg-emerald-50 rounded-xl px-4 py-3"
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span className="text-sm font-semibold text-slate-700">{item}</span>
            <span className="ml-auto text-xs font-extrabold text-emerald-600">Elegível</span>
          </motion.div>
        ))}
      </div>
    </motion.div>

    {/* Selo flutuante — mesma nota (4.9) já usada em TrustStats.js */}
    <motion.div
      initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
      animate={{ opacity: 1, scale: 1, rotate: -6 }}
      transition={{ duration: 0.5, delay: 1.2, type: 'spring', stiffness: 200 }}
      className="absolute -bottom-5 -left-5 sm:-left-8 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-2"
    >
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
        ))}
      </div>
      <span className="font-extrabold text-slate-800 text-sm">4.9</span>
    </motion.div>
  </div>
);

export const Hero = () => {
  const navigate = useNavigate();

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800"
    >
      {/* Decorative elements */}
      <div className="absolute top-20 -right-10 md:right-10 w-48 h-48 md:w-72 md:h-72 bg-white/5 rounded-full blur-2xl md:blur-3xl" />
      <div className="absolute bottom-10 -left-10 md:bottom-20 md:left-10 w-64 h-64 md:w-96 md:h-96 bg-blue-400/10 rounded-full blur-2xl md:blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 w-full">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-8 items-center">

          {/* Coluna de texto — alinhada à esquerda a partir do lg,
              quebrando a simetria centralizada que dominava a página inteira */}
          <div className="text-center lg:text-left">
            <Reveal delay={0.1} className="flex justify-center lg:justify-start mb-8">
              <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm text-white px-5 py-2.5 rounded-full">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-semibold text-yellow-400">Excelente</span>
                </div>
                <div className="w-px h-4 bg-white/30" />
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <div className="w-px h-4 bg-white/30" />
                <span className="text-white/90 text-sm">+15.000 clientes satisfeitos</span>
              </div>
            </Reveal>

            <Reveal
              as="h1"
              data-testid="hero-title"
              delay={0.2}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-bold text-white leading-tight tracking-tight mb-4 md:mb-6"
            >
              Seu voo atrasou ou foi cancelado?
              <br />
              <span className="text-yellow-300">
                Você pode receber até R$ 10.000!
              </span>
            </Reveal>

            <Reveal
              as="p"
              data-testid="hero-subtitle"
              delay={0.3}
              className="text-base sm:text-lg md:text-xl text-white/90 leading-relaxed max-w-2xl mx-auto lg:mx-0 mb-8 md:mb-10"
            >
              A gente resolve pra você, sem burocracia. É rápido, fácil e você
              <strong className="text-white"> só paga se ganhar</strong>. Descubra agora se você tem direito!
            </Reveal>

            <Reveal delay={0.4} className="flex flex-col items-center lg:items-start gap-6">
              <MotionButton
                data-testid="hero-cta-primary"
                onClick={() => navigate('/elegibilidade/voos')}
                size="lg"
                whileHover={{ y: -8 }}
                whileTap={{ scale: 0.97 }}
                className="bg-yellow-400 hover:bg-yellow-300 text-blue-900 rounded-full px-12 py-7 h-auto text-xl font-bold shadow-[0_8px_30px_rgba(250,204,21,0.3)] hover:shadow-[0_20px_40px_-15px_rgba(250,204,21,0.6)] transition-shadow duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] group"
              >
                Verifique GRÁTIS
                <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </MotionButton>
              <p className="text-white/70 text-sm flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Leva menos de 2 minutos
              </p>
            </Reveal>
          </div>

          {/* Coluna visual — some no mobile (empilhar um card estático
              abaixo do CTA só adicionaria altura sem ganho real numa tela
              pequena onde o usuário já está perto do botão) */}
          <div className="hidden lg:block">
            <PreviewCard />
          </div>
        </div>

        {/* Trust Indicators — faixa própria, largura cheia, abaixo das
            duas colunas */}
        <Reveal delay={0.6} className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto lg:mx-0 mt-12 md:mt-16">
          <div className="flex items-center justify-center lg:justify-start gap-3 bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 text-green-300" />
            </div>
            <div className="text-left">
              <p className="text-white font-semibold text-sm">100% Gratuito</p>
              <p className="text-white/70 text-xs">Sem custos iniciais</p>
            </div>
          </div>
          <div className="flex items-center justify-center lg:justify-start gap-3 bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-green-300" />
            </div>
            <div className="text-left">
              <p className="text-white font-semibold text-sm">Sem Riscos</p>
              <p className="text-white/70 text-xs">Só paga se ganhar</p>
            </div>
          </div>
          <div className="flex items-center justify-center lg:justify-start gap-3 bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-4">
            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-green-300" />
            </div>
            <div className="text-left">
              <p className="text-white font-semibold text-sm">98% de Sucesso</p>
              <p className="text-white/70 text-xs">Casos resolvidos</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
